import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from "@angular/core";
import { ApiService } from "app/services/api.service";
import { Router } from "@angular/router";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { StaffModel, IdentityUser } from "app/models/staff.model";
import { ValidateUsernameAsync } from "app/services/validators.service";
import { AlarmRouteModel, Route } from "../../../models/alarmroutes.model";
import { ReceiverTeam } from "../../../models/receiver-team";
import { HelperService } from "../../../services/helper.service";
import { UnlocDoorTypes } from "app/models/unloc.model";
import { Subject, forkJoin, of } from "rxjs";
import { catchError, throttleTime } from "rxjs/operators";
import { LockTypesPipe, MomentPipe } from "../../../pipes/pipes";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { CommonModule } from "@angular/common";
import { v4 as uuidv4 } from "uuid";
import { StaffAccessComponent } from "../staff-access/staff-access.component";
import { CanPipe } from "app/pipes/can.pipe";
import { PermissionService } from "app/services/permission.service";
import { Areas, IAccessRole, IAccessRoleSettings, IStaffAccess, PermissionGrants, resolveGrant } from "app/models/permission.model";
import {
  BUILT_IN_ROLE_OPTIONS,
  RoleGrantContext,
  builtInGrantsOf,
  canGrantBuiltIn,
  canGrantCustom,
  isEmaacRole,
  isRoleAboveOwnAccess,
  parseRoleChoice,
  roleChoice,
  visibleBuiltInRoles,
} from "../role-choice";
import { UserRoleLevel } from "app/models/user.model";
import {
  passwordValidator,
  passwordsMatchValidator,
} from "app/shared/password-validator.shared";

export interface StaffUpdateEvent {
  staff: StaffModel;
  updateUsername: boolean;
  /**
   * The staff member's custom role after the change, null for a built-in role; undefined when this
   * update doesn't know it (the list then relies on reloading the role symbols)
   */
  accessRoleId?: number | null;
}

@Component({
    selector: "staff-info",
    templateUrl: "./staff-info.component.html",
    styleUrls: ["./staff-info.component.css"],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        MatTooltipModule,
        TranslateModule,
        MomentPipe,
        LockTypesPipe,
        StaffAccessComponent,
        CanPipe
    ]
})
export class StaffInfoComponent implements OnChanges, OnDestroy, OnInit {
  constructor(
    private api: ApiService,
    private router: Router,
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private usernameValidator: ValidateUsernameAsync,
    private helper: HelperService,
    private permissions: PermissionService
  ) { }

  readonly Areas = Areas;

  @Input() staff: StaffModel;
  @Input() locations: any;
  @Output() closeStaffCardEvent = new EventEmitter<any>();
  @Output() staffUpdateEvent = new EventEmitter<StaffUpdateEvent>();
  @Output() staffInsertEvent = new EventEmitter<StaffModel>();
  @Output() staffDeleteEvent = new EventEmitter<string>();

  initialDataLoaded: boolean;
  alarmRouteLocations: AlarmRouteModel[] = [];
  availableAlarmRouteLocations: AlarmRouteModel[] = [];
  receiverTeamLocations: ReceiverTeamLocation[];
  availableReceiverTeamLocations: ReceiverTeamLocation[];
  user: any;
  editMode: boolean;
  staffEditForm: UntypedFormGroup;
  updateSuccess: boolean;
  updateError: boolean;
  updateErrorMessage: string | null = null;
  deleteConfirmation: boolean;
  enhancedSecurityFeature: boolean;
  identityUsers: IdentityUser[] = [];
  nonFederatedUsers: IdentityUser[] = [];
  identityUserSearchTerm = "";
  private identityUserOnPress: string | null = null;
  staffLocationList: string[];

  alarmRouteDropDownOpen: boolean = false;
  receiverTeamDropDownOpen: boolean = false;
  lockTypes = UnlocDoorTypes;
  //Select lock types by default which do not require special permissions
  selectedLockTypes: any;
  showUnlockSection = true;

  formSubmitSubject = new Subject<void>();
  isSwedishCustomer = false;

  // Roles (ES-7489)
  accessRoleSettings: IAccessRoleSettings | null = null;
  customRoles: IAccessRole[] = [];
  /** Built-in role grants by level, for users on a custom role who may only give what they have */
  private builtInGrants: Record<number, PermissionGrants> = {};
  /** Custom role currently assigned to the staff member, null for the built-in role of the level */
  staffAccessRoleId: number | null = null;
  /** The staff member's access (GET api/AccessRoles/Staff/{id}), loaded for users who may read access rights */
  private staffAccess: IStaffAccess | null = null;

  /** Built-in roles of the role selector (shared with the Access details panel) */
  readonly builtInRoleOptions = BUILT_IN_ROLE_OPTIONS;

  private getPasswordValidationMessage(err: any): string | null {
    if (err?.error?.code) {
      return this.translate.instant(err.error.code);
    }
    return err?.error?.errors?.[0]
      ? this.translate.instant(err.error.errors[0])
      : null;
  }

  createStaffEditForm(): void {
    const enhancedPasswordValidatorRegex = new RegExp(
      "^(?=.*\\S)[\\s\\S]{12,}$"
    );
    const gsmNumberRegex = new RegExp(
      "^\\+(9[976]\\d|8[987530]\\d|6[987]\\d|5[90]\\d|42\\d|3[875]\\d|2[98654321]\\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1)\\d{1,14}$"
    );

    this.staffEditForm = this.fb.group({
      firstName: this.staff.firstName,
      familyName: this.staff.familyName,
      username: [this.staff.username, { updateOn: "blur" }],
      location: [this.staff.location, Validators.required],
      accessLocations: [this.staffLocationList],
      alarmRoutes: [
        {
          value: this.staff.alarmRoutes,
          disabled: !this.staff.location && this.locations.length !== 1,
        },
      ],
      receiverTeams: [
        {
          value: this.staff.teams,
          disabled: !this.staff.location && this.locations.length !== 1,
        },
      ],
      gsmNumber: [
        this.staff.gsmNumber,
        { validators: Validators.pattern(gsmNumberRegex), updateOn: "blur" },
      ],
      email: [this.staff.email, Validators.email],
      language: [this.staff.language, Validators.required],
      usingLevel: [this.staff.usingLevel, Validators.required],
      lockType: [this.selectedLockTypes],
      password: [
        "",
        {
          validators: this.enhancedSecurityFeature
            ? Validators.pattern(enhancedPasswordValidatorRegex)
            : passwordValidator(),
          updateOn: "blur",
        },
      ],
      confirmPassword: [
        "",
        {
          validators: this.enhancedSecurityFeature
            ? Validators.pattern(enhancedPasswordValidatorRegex)
            : passwordValidator(),
          updateOn: "blur",
        },
      ],
      locked: this.staff.locked,
      identityUser: this.staff.identityUsername,
      temporaryCodeAccess: [this.staff.temporaryCodeAccess || false],
      accessRoleId: [this.staffAccessRoleId],
      // What the role selector shows; sets usingLevel or accessRoleId above
      role: [this.roleChoice(this.staffAccessRoleId, this.staff.usingLevel)],
    }, { validators: passwordsMatchValidator() });

    //Add required validators to password and confirm password fields if user is creating new staff and enhanced security feature is enabled
    if (!this.staff.id && this.enhancedSecurityFeature) {
      this.staffEditForm.controls["password"].setValidators([
        Validators.required,
        Validators.pattern(enhancedPasswordValidatorRegex)
      ]);

      this.staffEditForm.controls["password"].updateValueAndValidity();

      this.staffEditForm.controls["confirmPassword"].setValidators([
        Validators.required,
        Validators.pattern(enhancedPasswordValidatorRegex)
      ]);
      this.staffEditForm.controls["confirmPassword"].updateValueAndValidity();
    }

    if (this.staff.identityUsername) {
      //do not set validators if staff was integrated
    } else {
      this.staffEditForm.controls["username"].setValidators([
        Validators.required,
        Validators.pattern(new RegExp("^\\S+$")),
      ]);
      this.staffEditForm.controls["username"].setAsyncValidators(
        this.usernameValidator.validate.bind(this.usernameValidator)
      );
      this.staffEditForm.controls["username"].updateValueAndValidity();
    }

    if (this.locations.length === 1) {
      this.staffEditForm.get("location").setValue(this.locations[0]);
      this.staffEditForm.get("location").disable();
    }

    this.staffEditForm.get("location").valueChanges.subscribe((_) => {
      this.staffEditForm.get("alarmRoutes").enable();
      this.staffEditForm.get("alarmRoutes").reset([]);
      this.staffEditForm.get("receiverTeams").enable();
      this.staffEditForm.get("receiverTeams").reset([]);
      this.parseRoutes();
      this.parseReceiverTeams();
    });

    // Subscribe to lockType changes to update temporary code access state
    this.staffEditForm.get("lockType").valueChanges.subscribe((_) => {
      this.updateTemporaryCodeAccessState();
    });

    // Subscribe to usingLevel changes to update temporary code access state
    this.staffEditForm.get("usingLevel").valueChanges.subscribe((_) => {
      this.updateTemporaryCodeAccessState();
    });

    this.staffEditForm.get("role").valueChanges.subscribe((choice) => this.applyRoleChoice(choice));
    this.applyRoleChoice(this.staffEditForm.get("role").value, false);
    this.updateRoleControlState();

    // Initial check for temporary code access state
    this.updateTemporaryCodeAccessState();
  }

  /** Custom roles can be assigned: enabled, the user may manage access rights, and there are roles */
  get canAssignCustomRoles(): boolean {
    return !!this.accessRoleSettings?.customRolesEnabled &&
      this.permissions.has(Areas.StaffAccessRights, "ReadWrite") &&
      (this.customRoles.length > 0 || this.staffAccessRoleId !== null);
  }

  /** Role selector value: "custom:<id>" or "builtIn:<level>" */
  roleChoice(accessRoleId: number | null, level: number): string {
    return roleChoice(accessRoleId, level);
  }

  /** What the role rules need to know about the user giving roles */
  private get grantContext(): RoleGrantContext {
    return { userLevel: this.user?.roleLevel, permissions: this.permissions, builtInGrants: this.builtInGrants };
  }

  /** Built-in roles offered for this customer, plus the staff member's current one even without the feature */
  get visibleBuiltInRoles() {
    return visibleBuiltInRoles((f) => this.featureEnabled(f), { accessRoleId: this.staffAccessRoleId, level: this.staff?.usingLevel },
      this.user?.roleLevel);
  }

  /**
   * EMAAC Operators and EMAAC accounts are created, changed and removed by Everon admins only;
   * customers see them but don't edit them (the API refuses it)
   */
  get canEditStaffMember(): boolean {
    return !isEmaacRole(this.staff?.usingLevel) || this.user?.roleLevel === UserRoleLevel.SystemAdmin;
  }

  /** May the user give this built-in role (see role-choice.ts) */
  canGrantBuiltIn(level: number): boolean {
    return canGrantBuiltIn(this.grantContext, level);
  }

  /** May the user give this custom role: only with all of its access */
  canGrantCustom(role: IAccessRole): boolean {
    return canGrantCustom(this.grantContext, role);
  }

  /** The staff member has more access than the user, so the role can't be changed (the API refuses it) */
  get roleAboveOwnAccess(): boolean {
    if (!this.staff?.id) return false;
    return isRoleAboveOwnAccess(this.grantContext, { accessRoleId: this.staffAccessRoleId, level: this.staff.usingLevel }, this.customRoles);
  }

  private updateRoleControlState(): void {
    const control = this.staffEditForm?.get("role");
    if (!control) return;
    if (this.roleAboveOwnAccess) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

  /** A built-in role sets the level; a custom role keeps the level and is assigned after saving */
  private applyRoleChoice(choice: string, fromUser = true): void {
    const [kind, value] = (choice ?? "").split(":");
    const level = this.staffEditForm.get("usingLevel");
    const roleId = this.staffEditForm.get("accessRoleId");
    if (kind === "custom") {
      roleId.setValue(Number(value), { emitEvent: false });
      level.disable({ emitEvent: false });
    } else if (kind === "builtIn") {
      roleId.setValue(null, { emitEvent: false });
      level.enable({ emitEvent: false });
      level.setValue(Number(value));
      this.staff.usingLevel = Number(value);
    }
    if (fromUser) {
      roleId.markAsDirty();
    }
    // A custom role doesn't change the level, but may change what the staff member holds
    this.updateTemporaryCodeAccessState();
  }

  /** Load the custom roles and the staff member's assignment for the role selector */
  loadCustomRoles(): void {
    this.accessRoleSettings = null;
    this.customRoles = [];
    this.staffAccessRoleId = null;
    this.staffAccess = null;
    if (!this.permissions.has(Areas.StaffAccessRights, "Read")) return;

    forkJoin({
      settings: this.api.getAccessRoleSettings(),
      roles: this.api.getAccessRoles(),
      access: this.staff.id ? this.api.getStaffAccess(this.staff.id).pipe(catchError(() => of(null))) : of(null)
    }).pipe(catchError(() => of(null))).subscribe((res) => {
      if (!res) return;
      this.accessRoleSettings = res.settings;
      this.customRoles = res.roles.filter((r) => !r.builtIn);
      this.builtInGrants = builtInGrantsOf(res.roles);
      this.staffAccessRoleId = res.access?.accessRoleId ?? null;
      this.staffAccess = res.access ?? null;
      const control = this.staffEditForm?.get("role");
      if (control && control.pristine) {
        control.setValue(this.roleChoice(this.staffAccessRoleId, this.staff.usingLevel), { emitEvent: false });
        this.applyRoleChoice(control.value, false);
      }
      this.updateRoleControlState();
      this.updateTemporaryCodeAccessState();
    });
  }

  /** Assign the chosen custom role, or the built-in role of the level when the custom role was removed */
  async updateAccessRole(staffId: string, accessRoleId: number | null, usingLevel: number): Promise<void> {
    const assignment = accessRoleId ? { accessRoleId } : { builtInLevel: usingLevel };
    await new Promise<void>((resolve, reject) =>
      this.api.assignStaffAccessRole(staffId, assignment).subscribe({ next: () => resolve(), error: (err) => reject(err) })
    );
  }

  /**
   * The Access details panel changed the staff member's role or individual permissions: the user's own
   * map may have changed too, and a new role changes the staff member's level, shown here and in the list
   */
  onAccessChanged(access?: IStaffAccess): void {
    this.permissions.load(true).subscribe();
    if (!access || !this.staff) return;

    this.staffAccessRoleId = access.accessRoleId ?? null;
    this.staffAccess = access;
    if (access.level !== this.staff.usingLevel) {
      this.staff = { ...this.staff, usingLevel: access.level };
    }
    // Always report: a role change can keep the level (custom role to another custom role, or to
    // Read-only), and the list's role symbol must follow it
    this.staffUpdateEvent.emit({ staff: this.staff, updateUsername: false, accessRoleId: this.staffAccessRoleId });
  }

  unlockStaff() {
    this.staffEditForm.controls.locked.patchValue(false);
    this.submitStaffEdit();
  }

  compareByRouteId(o1: any, o2: any): boolean {
    return o1 && o2 && o1.id === o2.id;
  }

  compareByGroupId(o1: any, o2: any): boolean {
    return o1 && o2 && o1.groupId === o2.groupId;
  }

  closeStaffCard(): void {
    this.closeStaffCardEvent.emit(this.staff);
  }

  isFeatureEnabled(feature: string): boolean {
    const features: [string] = JSON.parse(localStorage.getItem("features"));
    if (features) {
      return features.includes(feature);
    }
    return false;
  }

  /**
   * An empty selection must never replace the linked authenticated user, and
   * Portal offers no way to unlink one. Put the current value back if it happens.
   */
  onIdentityUserSelectionChange(): void {
    const control = this.staffEditForm.controls.identityUser;
    if (!control.value) {
      control.setValue(this.staff.identityUsername || null);
      control.markAsPristine();
    }
  }

  onIdentityOptionPressed(): void {
    this.identityUserOnPress = this.staffEditForm.controls.identityUser.value;
  }

  /**
   * Clicking the selected authenticated user undoes an unsaved pick. A saved
   * authenticated user stays: Portal offers no way to remove one.
   */
  onIdentityOptionClicked(value: string): void {
    const pressedSelected = value === this.identityUserOnPress;
    this.identityUserOnPress = null;
    if (!pressedSelected || value === this.staff.identityUsername) {
      return;
    }
    // Material's single select ignores clicks on the selected option; reset once it has handled this one
    Promise.resolve().then(() => {
      const control = this.staffEditForm.controls.identityUser;
      control.setValue(this.staff.identityUsername || null);
      control.markAsPristine();
    });
  }

  /** True only when a real, different authenticated user has been chosen. */
  isAuthenticatedUserChanged(): boolean {
    const selected = this.staffEditForm.controls.identityUser.value;
    return !!selected && selected !== this.staff.identityUsername;
  }

  filterIdentityUsers(users: IdentityUser[]): IdentityUser[] {
    const searchTerm = this.identityUserSearchTerm.trim().toLowerCase();
    if (!searchTerm) {
      return users;
    }

    return users.filter((user) =>
      [user.firstname, user.familyName, user.username, user.email]
        .some((value) => value?.toLowerCase().includes(searchTerm))
    );
  }

  getLanguageString(lang: string) {
    switch (lang) {
      case "FI":
        return this.translate.instant("FINNISH");
      case "EN":
        return this.translate.instant("ENGLISH");
      case "ES":
        return this.translate.instant("SPANISH");
      case "SE":
        return this.translate.instant("SWEDISH");
      case "DE":
        return this.translate.instant("GERMAN");
      case "FR":
        return this.translate.instant("FRENCH");
      default:
        return this.translate.instant("UNKNOWN_LANGUAGE");
    }
  }

  getUsingLevelString(usingLevel: number): string {
    switch (usingLevel) {
      case 120:
        return this.translate.instant("STAFF_EMAAC_ACCOUNT");
      case 119:
        return this.translate.instant("STAFF_EMAAC_OPERATOR");
      case 101:
        return this.translate.instant("STAFF_CUSTOMER_ADMIN");
      case 100:
        return this.translate.instant("STAFF_MAIN_USER");
      case 50:
        return this.translate.instant("STAFF_USER");
      case 10:
        return this.translate.instant("EveronMobileApp");
      case 0:
        return this.translate.instant("STAFF_READONLY");
      default:
        "";
    }
  }

  featureEnabled(feature: string): boolean {
    const features = JSON.parse(localStorage.getItem("features"));
    if (features) {
      const index = features.indexOf(feature);
      if (index !== -1) {
        return true;
      }
    }
    return false;
  }

  hasMedicineCabinetAccess(): boolean {
    const selectedLockTypes = this.staffEditForm.get('lockType')?.value;
    return selectedLockTypes?.some((lockType: string) => ['12', '13', '14'].includes(lockType)) || false;
  }

  shouldShowTemporaryCodeAccess(): boolean {
    if (!this.isFeatureEnabled('DigitalKey')) {
      return false;
    }

    // If the staff member may manage temporary codes AND the viewer may too: always show
    if (this.staffManagesTemporaryCodes() && this.permissions.has(Areas.DevicesLocksTemporaryCodes, "ReadWrite")) {
      return true;
    }

    // If staff has medicine cabinet access: show
    if (this.hasMedicineCabinetAccess()) {
      return true;
    }

    return false;
  }

  /**
   * Does the staff member, with the role chosen in the form, hold ReadWrite on temporary codes: the
   * role's grants (built-in or custom) with the staff member's individual permissions on top. Without
   * the role data (the viewer can't read access rights) the built-in rule applies: customer admins.
   */
  private staffManagesTemporaryCodes(): boolean {
    const choice = parseRoleChoice(this.staffEditForm?.get("role")?.value ?? this.roleChoice(this.staffAccessRoleId, this.staff.usingLevel));
    let roleGrants: PermissionGrants | undefined;
    if (choice && "accessRoleId" in choice) {
      roleGrants = this.customRoles.find((r) => r.id === choice.accessRoleId)?.grants;
    } else {
      roleGrants = this.builtInGrants[choice && "builtInLevel" in choice ? choice.builtInLevel : this.staff.usingLevel];
    }
    if (!roleGrants) {
      // EMAAC Operators and accounts rank above 101 as numbers but have no access of their own
      return this.staff.usingLevel >= 101 && !isEmaacRole(this.staff.usingLevel);
    }
    const grants: PermissionGrants = { ...roleGrants, ...(this.staffAccess?.overrides ?? {}) };
    return resolveGrant(grants, Areas.DevicesLocksTemporaryCodes) === "ReadWrite";
  }

  isTemporaryCodeAccessDisabled(): boolean {
    // Only those who may manage temporary codes can edit (built-in roles: Superuser; and Everon admin)
    return !this.permissions.has(Areas.DevicesLocksTemporaryCodes, "ReadWrite");
  }

  updateTemporaryCodeAccessState(): void {
    if (this.staffEditForm) {
      const temporaryCodeControl = this.staffEditForm.get('temporaryCodeAccess');
      if (!this.shouldShowTemporaryCodeAccess() && temporaryCodeControl) {
        // disable checkbox
        temporaryCodeControl.disable();
      } else if (temporaryCodeControl) {
        if (this.isTemporaryCodeAccessDisabled()) {
          // If checkbox is visible but user can't edit it, disable it but keep current value
          temporaryCodeControl.disable();
        } else {
          // If checkbox is visible and user can edit it, enable it
          temporaryCodeControl.enable();
        }
      }
    }
  }

  staffEditMode(): void {
    //If staff does not have ID user is creating new staff so close the whole card
    if (!this.staff.id) {
      this.closeStaffCard();
      return;
    }
    //Hide all information and error messages
    this.updateError = false;
    this.updateSuccess = false;

    //If edit mode is not enabled create new form and enable edit mode.
    if (!this.editMode) {
      this.createStaffEditForm();
    }
    this.toggleEditMode(!this.editMode);
  }

  async createStaffInformation(data: any): Promise<any> {
    return await new Promise<any>((resolve, reject) =>
      this.api.addNewStaff(data).subscribe(
        (res) => {
          resolve(res);
        },
        (err) => reject(err)
      )
    );
  }

  async updateStaffInformation(data: any, newUuid: string): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      try {
        // Include current alarm routes and receiver teams to remove user from them, in case user is relocated from top level to a location
        // Only with the right to change those memberships (the API skips them otherwise)
        data.receiverTeamsToRemove = this.canEditReceiverTeams ? this.mapReceiverTeamsIdsToRemove(data) : [];
        data.alarmRoutesToRemove = this.canEditAlarmRoutes ? this.mapAlarmRoutesIdsToRemove(data) : [];

        // Remove fields in order to avoid errors in backend
        delete data.alarmRoutes;
        delete data.receiverTeams;

        this.api.saveStaffInfo(data, newUuid).subscribe(
          (res) => {
            resolve(res);
          },
          (err) => reject(err)
        );
      } catch (error) {
        console.error(error);
        reject();
      }
    });
  }

  async updateStaffAlarmRoutes(
    personnelId: string,
    alarmRoutes: Route[],
    newUuid: string = ""
  ): Promise<any> {
    let alarmRouteIds: string[] = [];
    if (alarmRoutes) {
      alarmRouteIds = alarmRoutes.map((alarmRoute) => alarmRoute.id);
    }

    await new Promise<any>((resolve, reject) =>
      this.api
        .updatePersonnelAlarmRoutes(personnelId, alarmRouteIds, newUuid)
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => reject()
        )
    );
  }

  async updateStaffReceiverTeams(
    personnelId: string,
    receiverTeams: ReceiverTeam[],
    newUuid: string = ""
  ): Promise<any> {
    const receiverTeamIds: { groups: string[] } = { groups: [] };
    if (receiverTeams) {
      receiverTeams.forEach((team) =>
        receiverTeamIds.groups.push(team.groupId)
      );
    }

    await new Promise<any>((resolve, reject) =>
      this.api
        .updatePersonnelReceiverTeams(personnelId, receiverTeamIds, newUuid)
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => reject()
        )
    );
  }

  async updateAuthenticatedUser(newUuid: string): Promise<any> {
    const selectedValue = this.staffEditForm.value.identityUser;
    if (!selectedValue) {
      // Never send an empty value: it would leave the staff member unable to log in
      return;
    }
    const selectedNonFed = this.nonFederatedUsers.find(u => u.staffId === selectedValue);

    if (selectedNonFed) {
      const data = { staffId: this.staff.id, staffIdToLink: selectedNonFed.staffId };
      await new Promise<any>((resolve, reject) =>
        this.api.linkNonFederatedUser(data, newUuid).subscribe(
          (res) => {
            this.staff.identityUsername = selectedNonFed.staffId;
            resolve(res);
          },
          (err) => reject(err)
        )
      );
    } else {
      const data = { username: selectedValue, personnelId: this.staff.id };
      await new Promise<any>((resolve, reject) =>
        this.api.updateAuthenticatedUser(data, newUuid).subscribe(
          (res) => {
            this.staff.identityUsername = selectedValue;
            resolve(res);
          },
          (err) => reject(err)
        )
      );
    }
  }

  async updateLocationAccess(newUuid: string): Promise<any> {
    const data = {
      personnelId: this.staff.id,
      locations: this.staffEditForm.value.accessLocations,
    };
    await new Promise<any>((resolve, reject) =>
      this.api.saveStaffLocationList(data, newUuid).subscribe(
        (res) => resolve(res),
        (err) => reject(err)
      )
    );
  }

  async updateLockOpeningRights(newUuid: string = ""): Promise<any> {
    const selectedLockTypes = this.staffEditForm.controls.lockType.value;
    const allLockTypes = UnlocDoorTypes;
    const data: any = [];
    // Check what lock types are selected and what are disabled
    allLockTypes.forEach((l) => {
      const lockType = {
        lockedDoorTypeId: parseInt(l.value),
        requirePermission: l.requirePermission,
        selected: false,
      };
      if (selectedLockTypes.includes(l.value)) {
        lockType.selected = true;
      }
      data.push(lockType);
    });

    await new Promise<any>((resolve, reject) =>
      this.api
        .updateStaffLockOpeningRights(this.staff.id, data, newUuid)
        .subscribe({
          next: (res) => {
            this.selectedLockTypes = selectedLockTypes;
            resolve(res);
          },
          error: (err) => reject(err)
        })
    );
  }

  async updateStaff(data: any): Promise<void> {
    const newUuid = uuidv4();

    if (this.isAuthenticatedUserChanged()) {
      await this.updateAuthenticatedUser(newUuid);
    }
    if (this.staffEditForm.controls.accessLocations.dirty) {
      await this.updateLocationAccess(newUuid);
    }
    if (this.staffEditForm.controls.alarmRoutes.dirty && this.canEditAlarmRoutes) {
      await this.updateStaffAlarmRoutes(data.id, data.alarmRoutes, newUuid);
    }
    if (this.staffEditForm.controls.receiverTeams.dirty && this.canEditReceiverTeams) {
      await this.updateStaffReceiverTeams(data.id, data.receiverTeams, newUuid);
    }

    if (this.staffEditForm.controls.lockType.dirty) {
      await this.updateLockOpeningRights(newUuid);
    }

    await this.updateStaffInformation(data, newUuid);

    if (this.staffEditForm.controls.accessRoleId.dirty && (this.staffEditForm.value.accessRoleId || this.staffAccessRoleId)) {
      await this.updateAccessRole(data.id, this.staffEditForm.value.accessRoleId, data.usingLevel);
    }
  }

  async saveNewStaff(data: any): Promise<any> {
    const newStaffResponse = await this.createStaffInformation(data);
    if (this.staffEditForm.value.accessRoleId) {
      await this.updateAccessRole(newStaffResponse.id, this.staffEditForm.value.accessRoleId, data.usingLevel);
    }
    // Routes and teams belong to their own areas; a new staff member has none to clear
    if (this.canEditAlarmRoutes && data.alarmRoutes?.length) {
      await this.updateStaffAlarmRoutes(newStaffResponse.id, data.alarmRoutes);
    }
    if (this.canEditReceiverTeams && data.receiverTeams?.length) {
      await this.updateStaffReceiverTeams(
        newStaffResponse.id,
        data.receiverTeams
      );
    }
    return newStaffResponse;
  }

  submitStaffEdit(): void {
    this.updateError = false;
    this.updateErrorMessage = null;
    this.updateSuccess = false;
    this.showUnlockSection = false;

    //Make deep copy of form data
    const data = JSON.parse(JSON.stringify(this.staffEditForm.value));
    const authenticatedUserChanged = this.isAuthenticatedUserChanged();

    // Store original temporaryCodeAccess value to check for modification
    const originalTemporaryCodeAccess = this.staff.temporaryCodeAccess || false;

    // If temporary code access checkbox is hidden, set it to false when saving
    if (!this.shouldShowTemporaryCodeAccess()) {
      data.temporaryCodeAccess = false;
    }

    // Check if temporaryCodeAccess was modified (changed from original value)
    const currentTemporaryCodeAccess = data.temporaryCodeAccess || false;
    data.temporaryCodeAccessModified = originalTemporaryCodeAccess !== currentTemporaryCodeAccess;

    //this.staffEditForm.value.username = "";

    data.id = this.staff.id;
    data.locationId = this.staffEditForm.getRawValue().location.id;
    // The level control is disabled while a custom role is chosen; send the level as it is
    data.usingLevel = this.staffEditForm.getRawValue().usingLevel;
    delete data.accessRoleId;
    delete data.role;

    if (this.staffEditForm.hasError("passwordsMismatch")) {
      return;
    }

    //When editing an existing staff member, only send a password if the user
    //actually entered one. Otherwise an empty password would be submitted and
    //rejected by the password policy, even though the password isn't changing.
    if (data.id && !data.password) {
      delete data.password;
      delete data.confirmPassword;
    }
    //If data already has ID user is editing staff details. Use right API for that and otherwise submit new staff
    if (data.id) {
      this.updateStaff(data).then(
        (_) => {
          //Get updated information from server if save was successfull
          this.api.getStaffInfo(data.id).subscribe((res) => {
            this.staff = res;
            this.updateSuccess = true;
            this.staffUpdateEvent.emit({
              staff: this.staff,
              updateUsername: !authenticatedUserChanged,
            });
            //Find correct location with location ID
            this.staff.location = (this.locations ?? []).find((obj) => {
              return obj.id === this.staff.locationId;
            });
          });
          this.toggleEditMode(false);
        },
        (err) => {
          this.updateError = true;
          this.updateErrorMessage = this.getPasswordValidationMessage(err);
          //Set username back into form in case of error in submit
          this.staffEditForm.value.username = data.username;
        }
      );
    } else {
      this.saveNewStaff(data).then(
        (response) => {
          //Update lock opening rights after save so we have staff ID
          if (this.staffEditForm.controls.lockType.dirty) {
            this.staff.id = response.id;
            this.updateLockOpeningRights();
          }

          //Get updated information from server if save was successfull
          this.api.getStaffInfo(response.id).subscribe((res) => {
            this.toggleEditMode(false);
            this.staffInsertEvent.emit(res);
          });
        },
        (err) => {
          this.updateError = true;
          this.updateErrorMessage = this.getPasswordValidationMessage(err);
        }
      );
    }
  }

  //Check if typed username is the same as the old one and mark field as pristine so it passes validation
  accountNameBlurMethod(): void {
    if (this.staffEditForm.value.username === this.staff.username) {
      this.staffEditForm.controls.username.markAsPristine();
      this.staffEditForm.controls.username.updateValueAndValidity();
    }
  }

  deleteStaff(): void {
    this.updateError = false;
    this.updateErrorMessage = null;
    this.updateSuccess = false;
    this.api.deleteStaff(this.staff.id).subscribe(
      (res) => {
        this.staffDeleteEvent.emit(this.staff.id);
      },
      (err) => {
        this.updateError = true;
        // 403: the staff member has more access than the user; 409: the company's last access rights admin
        this.updateErrorMessage = err?.status === 403 ? this.translate.instant("STAFF_DELETE_ABOVE_OWN_ACCESS")
          : err?.status === 409 ? this.translate.instant("STAFF_DELETE_LAST_ADMIN")
          : null;
        this.deleteConfirmation = false;
      }
    );
  }

  parseRoutes(): void {
    if (this.staffEditForm && this.staffEditForm.getRawValue().location) {
      const formLocationId = this.staffEditForm.getRawValue().location.id;
      if (formLocationId) {
        if (formLocationId.startsWith("C0")) {
          if (this.alarmRouteLocations) {
            this.availableAlarmRouteLocations = JSON.parse(
              JSON.stringify(this.alarmRouteLocations)
            );
          }
        } else {
          this.availableAlarmRouteLocations = [
            (this.alarmRouteLocations ?? []).find(
              (location) => location.locationId === formLocationId
            ),
          ];
        }
      }
    }
  }

  parseReceiverTeams(): void {
    if (this.staffEditForm && this.staffEditForm.getRawValue().location) {
      if (this.receiverTeamLocations) {
        const formLocationId = this.staffEditForm.getRawValue().location.id;
        if (formLocationId) {
          if (formLocationId.startsWith("C0")) {
            this.availableReceiverTeamLocations = JSON.parse(
              JSON.stringify(this.receiverTeamLocations)
            );
          } else {
            this.availableReceiverTeamLocations = [
              this.receiverTeamLocations.find(
                (location) => location.locationId === formLocationId
              ),
            ];
          }
        }
      }
    }
  }

  /** Alarm route recipients can be changed from the staff form */
  get canEditAlarmRoutes(): boolean {
    return this.permissions.has(Areas.AlarmRouting, "ReadWrite");
  }

  /** Receiver team memberships can be changed from the staff form */
  get canEditReceiverTeams(): boolean {
    return this.permissions.has(Areas.ReceiverTeams, "ReadWrite");
  }

  async getAlarmRoutes(): Promise<void> {
    // The routes feed only the route selector, shown to those who may change recipients
    if (!this.canEditAlarmRoutes) return;
    await new Promise<void>((resolve) =>
      this.api.getAlarmRoutes().subscribe({
        next: (res) => {
          if (res) {
            this.alarmRouteLocations = res;
          }
          resolve();
        },
        error: () => resolve()
      })
    );
  }

  async getReceiverTeams(): Promise<void> {
    const convertReceiverTeamsIntoLocations = (res: ReceiverTeam[]) => {
      if (!res) {
        return;
      }
      const receiverGroupRawLocations = this.helper.groupBy(
        res,
        (receiverTeam) => receiverTeam.locationId
      );

      this.receiverTeamLocations = receiverGroupRawLocations.map((teams) => {
        if (!teams[0].locationName) {
          return {
            locationName: teams[0].customerName,
            locationId: teams[0].locationId,
            receiverTeams: teams,
          };
        }
        return {
          locationName: teams[0].locationName,
          locationId: teams[0].locationId,
          receiverTeams: teams,
        };
      });
    };

    // The teams feed only the team selector, shown to those who may change memberships
    if (!this.canEditReceiverTeams) return;
    await new Promise<void>((resolve) =>
      this.api.getReceiverTeams().subscribe({
        next: (res) => {
          convertReceiverTeamsIntoLocations(res);
          resolve();
        },
        error: () => resolve()
      })
    );
  }

  async getIdentities(): Promise<void> {
    await new Promise<void>((resolve) =>
      forkJoin([
        this.api.getIdentityList().pipe(catchError(() => of([]))),
        this.api.getCompanyGroupNonFedStaff().pipe(catchError(() => of([])))
      ]).subscribe(([identities, nonFederatedRes]: [IdentityUser[], any]) => {
        this.identityUsers = identities;
        this.nonFederatedUsers = (nonFederatedRes || [])
          .filter((u: any) => u.username)
          .map((u: any) => ({ username: u.username, firstname: u.firstname, familyName: u.familyName, email: u.email, staffId: u.staffId }));
        resolve();
      })
    );
  }

  async getLockOpeningRights(): Promise<void> {
    if (!this.staff.id) {
      this.selectedLockTypes = UnlocDoorTypes.map((x) =>
        x.requirePermission === false ? x.value : undefined
      );
      return;
    }

    // Lock opening rights are part of the locks
    if (!this.permissions.has(Areas.DevicesLocks, "Read")) {
      this.selectedLockTypes = [];
      return;
    }

    await new Promise<void>((resolve) =>
      this.api.getStaffLockOpeningRights(this.staff.id).subscribe({
        next: (res) => {
          this.selectedLockTypes = (res ?? []).map((x) => {
            if (x.permitted) {
              return x.lockedDoorTypeId.toString();
            }
          });
          resolve();
        },
        error: () => {
          this.selectedLockTypes = [];
          resolve();
        }
      })
    );
  }

  async getStaffLocationList(): Promise<void> {
    await new Promise<void>((resolve) => {
      // Skip fetching the location access list for the new user
      if (!this.staff.id) {
        resolve();
        return;
      }

      this.api.getStaffLocationList(this.staff.id).subscribe({
        next: (res) => {
          if (res) {
            this.staffLocationList = res;
          }
          resolve();
        },
        error: () => resolve()
      })
    });
  }

  async fetchInitialData(): Promise<void> {
    await this.getReceiverTeams();
    await this.getAlarmRoutes();
    // Identity links and location access lists are access rights management
    if (
      this.isFeatureEnabled("StrongAuthentication") && this.permissions.has(Areas.StaffAccessRights, "Read")
    ) {
      await this.getIdentities();
    }
    if (this.permissions.has(Areas.StaffAccessRights, "Read")) {
      await this.getStaffLocationList();
    }
  }

  openAlarmRoutePage(routeId: string): void {
    this.router.navigate(["route/" + routeId]);
  }

  openReceiverTeamsPage(teamId: string): void {
    this.router.navigate(["teams/" + teamId]);
  }

  openRoles(): void {
    this.router.navigate(["/roles"]);
  }

  //Toggle editMode, If edit mode is true fetch AlarmRoutes and ReceiverTeams
  toggleEditMode(editMode: boolean) {
    this.editMode = editMode;
    if (editMode) {
      this.initialDataLoaded = false;
      this.fetchInitialData().then((_) => {
        this.createStaffEditForm();
        this.parseRoutes();
        this.parseReceiverTeams();
        this.initialDataLoaded = true;
      });
    }
  }

  mapAlarmRoutesIdsToRemove(data: any): string[] {
    return this.staff?.alarmRoutes
      ?.map((route) => route.id)
      .filter(
        (routeId) =>
          !data.alarmRoutes.map((route: Route) => route.id).includes(routeId)
      ); // Do not include routes to the remove list that are about to be reassigned
  }

  mapReceiverTeamsIdsToRemove(data: any): string[] {
    return this.staff?.teams
      ?.map((team) => team.groupId)
      .filter(
        (teamId) =>
          !data.receiverTeams
            .map((team: ReceiverTeam) => team.groupId)
            .includes(teamId)
      );
  }

  async ngOnInit(): Promise<void> {
    const customerInfo = this.helper.getCustomerInfo();
    this.isSwedishCustomer = customerInfo?.country === 'SE';
    if (this.isFeatureEnabled("DigitalKey")) {
      await this.getLockOpeningRights();
    }

    this.createStaffEditForm();
    // Initialize form submit subject with 3 second throttle time to prevent multiple submits
    this.formSubmitSubject.pipe(throttleTime(3000)).subscribe(() => {
      this.submitStaffEdit();
    });
  }


  ngOnChanges() {
    this.enhancedSecurityFeature = this.isFeatureEnabled("EnhancedSecurity");
    this.toggleEditMode(false);
    this.updateError = false;
    this.updateSuccess = false;

    // Find correct location from location list with location ID
    this.staff.location = (this.locations ?? []).find((obj) => {
      return obj.id === this.staff.locationId;
    });

    this.user = JSON.parse(localStorage.getItem("user"));

    // Role selector data for this staff member
    this.loadCustomRoles();

    // If staff does not have ID user is creating new staff. Enable editing mode and initialize the edit form
    if (!this.staff.id) {
      this.toggleEditMode(true);
    }
  }

  ngOnDestroy(): void {
    this.formSubmitSubject.unsubscribe();
  }
}

interface ReceiverTeamLocation {
  locationName: string;
  locationId: string;
  receiverTeams: ReceiverTeam[];
}
